// Environment configuration loaded from import.meta.env
// These values come from .env files or build-time environment variables
// Taxonomy options are now loaded from the API (/api/taxonomy) instead of being hardcoded

export type SearchDomain = 'demo' | 'logo';

export interface DomainSearchConfig {
  searchHub: string;
  pipeline: string;
  filterField: string;
  filterValue: string;
  analyticsOriginLevel2: string;
}

const demoSearchConfig: DomainSearchConfig = {
  searchHub:
    import.meta.env.VITE_DEMO_COVEO_SEARCH_HUB ||
    import.meta.env.VITE_COVEO_SEARCH_HUB ||
    'DemoInventoryFinder',
  pipeline:
    import.meta.env.VITE_DEMO_COVEO_PIPELINE ||
    import.meta.env.VITE_COVEO_PIPELINE ||
    'default',
  filterField: import.meta.env.VITE_DEMO_COVEO_FILTER_FIELD || 'record_type',
  filterValue: import.meta.env.VITE_DEMO_COVEO_FILTER_VALUE || 'demo',
  analyticsOriginLevel2: 'DemoSearchPage',
};

const logoSearchConfig: DomainSearchConfig = {
  searchHub: import.meta.env.VITE_LOGO_COVEO_SEARCH_HUB || 'LogoSearch',
  pipeline: import.meta.env.VITE_LOGO_COVEO_PIPELINE || 'LogoSearch',
  filterField: import.meta.env.VITE_LOGO_COVEO_FILTER_FIELD || 'record_type',
  filterValue: import.meta.env.VITE_LOGO_COVEO_FILTER_VALUE || 'logo',
  analyticsOriginLevel2: 'LogoSearchPage',
};

export const getDomainSearchConfig = (domain: SearchDomain): DomainSearchConfig =>
  domain === 'logo' ? logoSearchConfig : demoSearchConfig;

export const config = {
  // API
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  
  // Microsoft Entra ID (Azure AD) SSO
  microsoftClientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '',
  microsoftTenantId: import.meta.env.VITE_MICROSOFT_TENANT_ID || '',
  microsoftRedirectUri:
    import.meta.env.VITE_MICROSOFT_REDIRECT_URI || (typeof window !== 'undefined' ? window.location.origin : ''),
  microsoftPostLogoutUri:
    import.meta.env.VITE_MICROSOFT_POST_LOGOUT_URI || (typeof window !== 'undefined' ? window.location.origin : ''),
  
  // Coveo - use API key directly (no token fetching)
  coveoOrgId: import.meta.env.VITE_COVEO_ORG_ID || '',
  coveoSearchApiUrl: import.meta.env.VITE_COVEO_SEARCH_API_URL || 'https://platform.cloud.coveo.com/rest/search/v3',
  coveoSearchApiKey: import.meta.env.VITE_COVEO_SEARCH_API_KEY || '',
  coveoSearchHub: demoSearchConfig.searchHub,
  coveoPipeline: demoSearchConfig.pipeline,
  domainSearch: {
    demo: demoSearchConfig,
    logo: logoSearchConfig,
  },
  
  // App
  allowedEmailDomain: import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN || 'coveo.com',
  searchDebounceMs: parseInt(import.meta.env.VITE_SEARCH_DEBOUNCE_MS || '300', 10),
  defaultPageSize: parseInt(import.meta.env.VITE_DEFAULT_PAGE_SIZE || '30', 10),
  facetDefaultValuesCount: parseInt(import.meta.env.VITE_FACET_DEFAULT_VALUES_COUNT || '5', 10),
  
  // Sort Options
  sortOptions: [
    { value: 'relevance', label: 'Relevance' },
    { value: 'date descending', label: 'Newest First' },
    { value: 'date ascending', label: 'Oldest First' },
    { value: '@title ascending', label: 'Title (A-Z)' },
    { value: '@title descending', label: 'Title (Z-A)' },
  ],

  // NOTE: The following are deprecated fallbacks.
  // Taxonomy options should be loaded from the API (/api/taxonomy)
  // These are kept for backward compatibility only.

  // Demo Tiers (fallback)
  tiers: ['A', 'B'],

  // Source Types (fallback)
  sourceTypes: [
    'Live Sites',
    'Barca',
    'Custom Demo',
    'ISV',
  ],

  // Platforms (fallback - use taxonomy API instead)
  platforms: [],

  // Use Cases (fallback - use taxonomy API instead)
  useCases: [],

  // Functionalities (fallback - use taxonomy API instead)
  functionalities: [],

  // Industries (fallback - use taxonomy API instead)
  industries: [],

  // Personas (fallback - use taxonomy API instead)
  personas: [],
};

export default config;
